import React from "react";
import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Grid from "@cloudscape-design/components/grid";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import DatePicker from "@cloudscape-design/components/date-picker";
import Tabs from "@cloudscape-design/components/tabs";
import Container from "@cloudscape-design/components/container";
import SpaceBetween from "@cloudscape-design/components/space-between";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Multiselect from "@cloudscape-design/components/multiselect";
import StatusIndicator from "@cloudscape-design/components/status-indicator";

import ProgressiveImage from "react-progressive-graceful-image";
import Poster from "../assets/poster.png";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { achievementStore } from "../stores/achievement";
import { configStore } from "../stores/config";
import { achievementCategoryStore } from "../stores/achievementCategory";
import { achievementCategoryAddModalStore } from "../stores/achievementCategoryAddModal";
import { achievementCategoryEditModalStore } from "../stores/achievementCategoryEditModal";
import { achievementAddModalStore } from "../stores/achievementAddModal";
import { achievementEditModalStore } from "../stores/achievementEditModal";
import { downloadTableExport } from "../helpers/tableExport";

const numberFormatter = new Intl.NumberFormat("ru-RU");

const EXPORT_FORMAT_OPTIONS = [
	{ value: "csv", label: "CSV" },
	{ value: "json", label: "JSON" },
	{ value: "xlsx", label: "XLSX" },
];

const EXPORT_STATUS_OPTIONS = [
	{ value: "", label: "All" },
	{ value: "1", label: "Active (Enabled)" },
	{ value: "0", label: "Disabled" },
];

const EXPORT_SOURCE_TYPE_OPTIONS = [
	{ value: "deposit", label: "Deposit" },
	{ value: "withdrawal", label: "Withdrawal" },
	{ value: "registration", label: "Registration" },
	{ value: "verify", label: "Verify" },
	{ value: "email_verified", label: "Email verified" },
	{ value: "phone_verified", label: "Phone verified" },
	{ value: "tg_verified", label: "Telegram verified" },
	{ value: "vip_lvl", label: "Vip lvl" },
	{ value: "spins", label: "Spins" },
	{ value: "mobile", label: "Mobile" },
	{ value: "referral", label: "Referral" },
	{ value: "tasks", label: "Tasks" },
	{ value: "combined", label: "Combined" },
];

const EXPORT_ACHIEVEMENT_TYPE_OPTIONS = [
	{ value: "daily", label: "Daily" },
	{ value: "unique", label: "Unique" },
	{ value: "progressive", label: "Progressive" },
	{ value: "daily_progressive", label: "Daily progressive" },
];

const EXPORT_PRIZE_TYPE_OPTIONS = [
	{ value: "freespins", label: "FS" },
	{ value: "chest", label: "Chests" },
	{ value: "tickets", label: "Ticket" },
	{ value: "money", label: "Money" },
];

const EXPORT_COLUMN_OPTIONS = [
	{ value: "id", label: "ID" },
	{ value: "imageAddress", label: "Image Address" },
	{ value: "name", label: "Name" },
	{ value: "description", label: "Description" },
	{ value: "sourceType", label: "Source Type" },
	{ value: "achievementType", label: "Achievement Type" },
	{ value: "startDate", label: "Start Date" },
	{ value: "endDate", label: "End Date" },
	{ value: "active", label: "Active" },
	{ value: "levelOrDay", label: "Level/Day" },
	{ value: "prizeType", label: "Prize Type" },
	{ value: "prizeValue", label: "Prize Value" },
	{ value: "fsBet", label: "FS Bet" },
];

const EXPORT_DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Set a custom range",
	customRelativeRangeUnitLabel: "Unit",
	formatRelativeRange: (value) => value,
	formatUnit: (unit, value) => `${value} ${unit}`,
	dateTimeConstraintText: "Use YYYY/MM/DD.",
	relativeModeTitle: "Relative range",
	absoluteModeTitle: "Absolute range",
	relativeRangeSelectionHeading: "Choose a range",
	startDateLabel: "Start date",
	endDateLabel: "End date",
	startTimeLabel: "Start time",
	endTimeLabel: "End time",
	clearButtonLabel: "Clear",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
};

const getExportOption = (options, value) => options.find((option) => option.value === value) || options[0];

const validateExportDateRange = (range) => {
	if (range?.type !== "absolute" || !range.startDate || !range.endDate) {
		return { valid: false, errorMessage: "Select both start and end dates" };
	}
	return range.startDate <= range.endDate
		? { valid: true }
		: { valid: false, errorMessage: "Start date must be before or equal to end date" };
};

class Achievement extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTabId: "achievement",
			exportModalVisible: false,
			exportFormat: "csv",
			exportId: "",
			exportName: "",
			exportStatus: "",
			exportSourceTypes: [],
			exportAchievementTypes: [],
			exportPrizeTypes: [],
			exportColumns: [...EXPORT_COLUMN_OPTIONS],
			exportCreatedAtRange: null,
			exportActivePeriodRange: null,
			exportLoading: false,
			exportError: "",
			statisticsPeriod: "day",
			statisticsStartDate: "",
			statisticsEndDate: "",
			statisticsLoading: false,
			statisticsError: null,
			statistics: {
				total: [],
				single: [],
				progressive: [],
				daily_progressive: [],
			},
		};
	}

	componentDidMount() {
		this.props.Interface.loadAchievementList();
	}

	resetExportFilters = () => {
		this.setState({
			exportFormat: "csv",
			exportId: "",
			exportName: "",
			exportStatus: "",
			exportSourceTypes: [],
			exportAchievementTypes: [],
			exportPrizeTypes: [],
			exportColumns: [...EXPORT_COLUMN_OPTIONS],
			exportCreatedAtRange: null,
			exportActivePeriodRange: null,
			exportError: "",
		});
	};

	exportAchievements = async () => {
		if (this.state.exportLoading) return;

		const createdAtRange = this.state.exportCreatedAtRange?.type === "absolute" ? this.state.exportCreatedAtRange : null;
		const activePeriodRange = this.state.exportActivePeriodRange?.type === "absolute" ? this.state.exportActivePeriodRange : null;
		if (!createdAtRange || !createdAtRange.startDate || !createdAtRange.endDate) {
			this.setState({ exportError: "Created At is required" });
			return;
		}

		if (!this.state.exportColumns.length) {
			this.setState({ exportError: "Select at least one export column" });
			return;
		}

		this.setState({ exportLoading: true, exportError: "" });

		try {
			const rows = await this.props.Interface.loadAchievementExport({
				id: this.state.exportId.trim(),
				name: this.state.exportName.trim(),
				active: this.state.exportStatus,
				sourceTypes: this.state.exportSourceTypes.map((option) => option.value),
				achievementTypes: this.state.exportAchievementTypes.map((option) => option.value),
				prizeTypes: this.state.exportPrizeTypes.map((option) => option.value),
				createdAtFrom: createdAtRange.startDate,
				createdAtTo: createdAtRange.endDate,
				activeDateFrom: activePeriodRange?.startDate || "",
				activeDateTo: activePeriodRange?.endDate || "",
			});

			if (!rows.length) {
				this.setState({ exportError: "No achievements match the selected filters" });
				return;
			}

			downloadTableExport({
				rows,
				columns: this.state.exportColumns,
				format: this.state.exportFormat,
				filename: `achievements_export_${new Date().toISOString().replace(/[:.]/g, "-")}.${this.state.exportFormat}`,
			});
			this.setState({ exportModalVisible: false });
		} catch (error) {
			this.setState({ exportError: error?.text || error?.message || String(error) || "Failed to export achievements" });
		} finally {
			this.setState({ exportLoading: false });
		}
	};

	loadStatistics = async () => {
		const { statisticsPeriod, statisticsStartDate, statisticsEndDate } = this.state;

		if (!statisticsStartDate || !statisticsEndDate) {
			this.setState({ statisticsError: "Select start and end date" });
			return;
		}

		this.setState({ statisticsLoading: true, statisticsError: null });

		try {
			const response = await this.props.Interface.loadAchievementStatistics({
				period: statisticsPeriod,
				start_date: statisticsStartDate,
				end_date: statisticsEndDate,
			});

			this.setState({
				statisticsLoading: false,
				statistics: response || { total: [], single: [], progressive: [], daily_progressive: [] },
			});
		} catch (err) {
			this.setState({
				statisticsLoading: false,
				statisticsError: err.message || "Failed to load statistics",
			});
		}
	};

	formatPeriod = (period) => {
		if (!period) return "-";

		if (period.includes(" - ")) {
			const [start, end] = period.split(" - ");
			const formatDate = (d) => {
				const [y, m, day] = d.split(".");
				return `${day}.${m}.${y}`;
			};
			return `${formatDate(start)} - ${formatDate(end)}`;
		}

		const parts = period.split(".");
		if (parts.length === 3) {
			const [y, m, d] = parts;
			return `${d}.${m}.${y}`;
		}
		if (parts.length === 2) {
			const [y, m] = parts;
			return `${m}.${y}`;
		}

		return period;
	};

	fmt = (v) => (v != null ? numberFormatter.format(v) : "0");
	fmtMoney = (cents, isFs = false) => (cents > 0 ? `${numberFormatter.format(Math.round(isFs ? cents : cents / 100 ))} RUB.` : "0 RUB.");
	fmtTickets = (n) => (n > 0 ? `${this.fmt(n)} tickets` : "0 tickets");
	fmtFs = (n) => (n > 0 ? `${this.fmt(n)} FS` : "0 FS");
	fmtPercent = (v) => (v == null ? "—" : `${v}%`);

	renderStatsTable = (title, data, achievementId = null, level = null, levelLabel = "Level", showFullCompletions = false) => {
		if (!data || data.length === 0) return null;

		const periods = [...new Set(data.map(r => r.period))];

		const summary = data.reduce((acc, row) => {
			acc.views += Number(row.views || 0);
			acc.views_unique += Number(row.views_unique || 0);
			acc.started += Number(row.started || 0);
			acc.completed += Number(row.completed || 0);
			acc.full_completed += Number(row.full_completed || 0);
			acc.started_unique += Number(row.started_unique || 0);
			acc.completed_unique += Number(row.completed_unique || 0);
			acc.full_completed_unique += Number(row.full_completed_unique || 0);
			acc.money += Number(row.money || 0);
			acc.tickets += Number(row.tickets || 0);
			acc.freespins += Number(row.freespins || 0);
			acc.freespins_total += Number(row.freespins_total || 0);
			acc.chests_count += Number(row.chests_count || 0);
			acc.chest_money += Number(row.chest_money || 0);
			acc.chest_tickets += Number(row.chest_tickets || 0);
			acc.chest_freespins += Number(row.chest_freespins || 0);
			acc.chest_freespins_total += Number(row.chest_freespins_total || 0);


			if (row.avg_days != null && row.completed > 0) {
				acc.totalDays += row.avg_days * row.completed;
				acc.totalCompletedForAvg += row.completed;
			}
			return acc;
		}, {
			views: 0,
			views_unique: 0,
			started: 0,
			completed: 0,
			full_completed: 0,
			started_unique: 0,
			completed_unique: 0,
			full_completed_unique: 0,
			money: 0,
			tickets: 0,
			freespins: 0,
			freespins_total: 0,
			chests_count: 0,
			chest_money: 0,
			chest_tickets: 0,
			chest_freespins: 0,
			chest_freespins_total: 0,
			totalDays: 0,
			totalCompletedForAvg: 0,
		});


		const avgDaysSummary = summary.totalCompletedForAvg > 0
			? (summary.totalDays / summary.totalCompletedForAvg).toFixed(1)
			: "—";


		const viewStartRate = summary.views > 0
			? ((summary.started / summary.views) * 100).toFixed(1)
			: "0.0";

		const viewStartRateUnique = summary.views_unique > 0
			? ((summary.started_unique / summary.views_unique) * 100).toFixed(1)
			: "0.0";

		const completionRate = data.some(row => row.completion_rate_pct == null)
			? null
			: summary.started > 0
			? ((summary.completed / summary.started) * 100).toFixed(1)
			: "0.0";

		const completionRateUnique = data.some(row => row.completion_rate_unique_pct == null)
			? null
			: summary.started_unique > 0
			? ((summary.completed_unique / summary.started_unique) * 100).toFixed(1)
			: "0.0";

		const viewCompletionRate = summary.views > 0
			? ((summary.completed / summary.views) * 100).toFixed(1)
			: "0.0";

		const viewCompletionRateUnique = summary.views_unique > 0
			? ((summary.completed_unique / summary.views_unique) * 100).toFixed(1)
			: "0.0";

		const rows = [
			{ metric: "Views", key: "views" },
			{ metric: "Views (Unique)", key: "views_unique" },
			{ metric: "Started", key: "started" },
			{ metric: "Started (Unique)", key: "started_unique" },
			{ metric: "Completed", key: "completed" },
			{ metric: "Completed (Unique)", key: "completed_unique" },
			...(showFullCompletions ? [
				{ metric: "Full Completions", key: "full_completed" },
				{ metric: "Full Completions (Unique)", key: "full_completed_unique" },
			] : []),
			{ metric: "View to Start %", key: "view_start_rate_pct", format: this.fmtPercent },
			{ metric: "View to Start (Unique) %", key: "view_start_rate_unique_pct", format: this.fmtPercent },
			{ metric: "Start to Complete %", key: "completion_rate_pct", format: this.fmtPercent },
			{ metric: "Start to Complete (Unique) %", key: "completion_rate_unique_pct", format: this.fmtPercent },
			{ metric: "View to Complete %", key: "view_completion_rate_pct", format: this.fmtPercent },
			{ metric: "View to Complete (Unique) %", key: "view_completion_rate_unique_pct", format: this.fmtPercent },
			{ metric: "Avg Days", key: "avg_days", format: v => v == null ? "—" : v },
			{ metric: "Money Rewarded", key: "money", format:  v => this.fmtMoney(v, false) },
			{ metric: "Tickets Issued", key: "tickets", format: v => this.fmtTickets(v || 0) },
			{
				metric: "Freespins Issued",
				key: "freespins",
				format: (v, row) => {
					const spins = v || 0;
					const volume = row?.freespins_total || 0;
					if (spins === 0) return "-";
					return volume > 0
						? `${this.fmtFs(spins)} (${this.fmtMoney(volume, true)})`
						: this.fmtFs(spins);
				}
			},
			{ metric: "Chests", key: "chests_count" },
			{ metric: "Chest Money", key: "chest_money", format: v => this.fmtMoney(v, false) },
			{ metric: "Chest Tickets", key: "chest_tickets", format: v => this.fmtTickets(v || 0) },
			{ metric: "Chest Freespins", key: "chest_freespins", format: v => this.fmtFs(v || 0) },
			{ metric: "Chest Freespins Total", key: "chest_freespins_total", format: v => this.fmtMoney(v, true) },
		];

		return (
			<Box margin={{ top: "xl", bottom: "xxl" }}>
				<Table
					header={
						<Header variant="h3">
							{title}
							{achievementId && ` (ID: ${achievementId})`}
							{level !== null && ` — ${levelLabel} ${level}`}
						</Header>
					}
					columnDefinitions={[
						{
							id: "metric",
							header: "Metric",
							cell: item => <Box fontWeight="bold">{item.metric}</Box>,
							width: 280
						},
						...periods.map((period, i) => ({
							id: `period-${i}`,
							header: <Box textAlign="center" fontWeight="bold">{this.formatPeriod(period)}</Box>,
							cell: item => {
								const rowData = data.find(d => d.period === period) || {};
								const value = rowData[item.key];
								const formatted = item.format
									? item.format(value, rowData)
									: this.fmt(value);
								return <Box textAlign="center">{formatted}</Box>;
							},
						})),
						{
							id: "summary",
							header: <Box textAlign="center" fontWeight="bold" color="text-status-info">Summary</Box>,
							cell: item => {
								let value;
								switch (item.key) {
									case "views": value = summary.views; break;
									case "views_unique": value = summary.views_unique; break;
									case "started": value = summary.started; break;
									case "completed": value = summary.completed; break;
									case "full_completed": value = summary.full_completed; break;
									case "view_start_rate_pct": value = viewStartRate; break;
									case "view_start_rate_unique_pct": value = viewStartRateUnique; break;
									case "completion_rate_pct": value = completionRate; break;
									case "started_unique": value = summary.started_unique; break;
									case "completed_unique": value = summary.completed_unique; break;
									case "full_completed_unique": value = summary.full_completed_unique; break;
									case "completion_rate_unique_pct": value = completionRateUnique; break;
									case "view_completion_rate_pct": value = viewCompletionRate; break;
									case "view_completion_rate_unique_pct": value = viewCompletionRateUnique; break;
									case "avg_days": value = avgDaysSummary; break;
									case "money": value = summary.money; break;
									case "tickets": value = summary.tickets; break;
									case "chests_count": value = summary.chests_count; break;
									case "chest_money": value = summary.chest_money; break;
									case "chest_tickets": value = summary.chest_tickets; break;
									case "chest_freespins": value = summary.chest_freespins; break;
									case "chest_freespins_total": value = summary.chest_freespins_total; break;
									case "freespins":
										const spins = summary.freespins;
										const volume = summary.freespins_total;
										if (spins === 0) return <Box textAlign="center">-</Box>;
										return (
											<Box color="text-status-success" fontWeight="bold" textAlign="center">
												{volume > 0
													? `${this.fmtFs(spins)} (${this.fmtMoney(volume, true)})`
													: this.fmtFs(spins)
												}
											</Box>
										);
									default: value = 0;
								}

								const formatted = item.format
									? item.format(value, summary)
									: this.fmt(value);

								return (
									<Box textAlign="center" fontWeight="bold" color="text-status-success">
										{formatted}
									</Box>
								);
							},
						}
					]}
					items={rows}
				/>
			</Box>
		);
	};

	renderAnalyticsTab = () => {
		const { statistics, statisticsLoading, statisticsError, statisticsPeriod, statisticsStartDate, statisticsEndDate } = this.state;
		const dailyProgressiveStatistics = statistics.daily_progressive || statistics.dailyProgressive || [];
		const hasDetailedStatistics = statistics.single?.length > 0 || statistics.progressive?.length > 0 || dailyProgressiveStatistics.length > 0;

		return (
			<>
			<Container header={<Header variant="h2">Achievement Statistics</Header>}>
				<Box margin={{ top: "m", bottom: "m" }}>
					<Grid gridDefinition={[{ colspan: 12 }]}>
						<SpaceBetween direction="horizontal" size="xs">
							<Select
								selectedOption={{
									value: statisticsPeriod,
									label: statisticsPeriod === "day" ? "Day" : statisticsPeriod === "week" ? "Week" : "Month"
								}}
								onChange={({ detail }) => this.setState({ statisticsPeriod: detail.selectedOption.value })}
								options={[
									{ value: "day", label: "Day" },
									{ value: "week", label: "Week" },
									{ value: "month", label: "Month" }
								]}
							/>

							<DatePicker
								value={statisticsStartDate}
								onChange={({ detail }) => this.setState({ statisticsStartDate: detail.value })}
								placeholder="Start date"
							/>

							<DatePicker
								value={statisticsEndDate}
								onChange={({ detail }) => this.setState({ statisticsEndDate: detail.value })}
								placeholder="End date"
							/>


							<Button variant="primary" onClick={this.loadStatistics}>
								Submit
							</Button>
						</SpaceBetween>
					</Grid>
				</Box>


				{statisticsError && <Alert type="error">{statisticsError}</Alert>}

			</Container>
				{statisticsLoading ? (
					<Box textAlign="center" padding="xxl">
						<Spinner size="large" />
					</Box>
				) : (
					<>
						{statistics.total?.length > 0 && this.renderStatsTable("TOTAL (All Achievements)", statistics.total, null, null, "Level", true)}


						{hasDetailedStatistics ? <>

								<Tabs
									tabs={[
										{
											label: `Single Achievements ${statistics.single?.length > 0 ? `(${statistics.single.length})` : ''}`,
											id: "single",
											content: statistics.single?.length > 0 ? (
												<Box margin={{ top: "m" }}>
													{Object.values(
														statistics.single.reduce((acc, row) => {
															const key = row.achievement_id;
															if (!acc[key]) {
																acc[key] = { name: row.achievement_name, id: row.achievement_id, data: [] };
															}
															acc[key].data.push(row);
															return acc;
														}, {})
													).map(group => this.renderStatsTable(group.name, group.data, group.id))}
												</Box>
											) : (
												<Box textAlign="center" color="text-label" padding="l">
													No single achievements data
												</Box>
											),
										},
										{
											label: `Progressive Achievements ${statistics.progressive?.length > 0 ? `(${statistics.progressive.length})` : ''}`,
											id: "progressive",
											content: statistics.progressive?.length > 0 ? (
												<Box margin={{ top: "m" }}>
													{Object.values(
														statistics.progressive.reduce((acc, row) => {
															const key = row.achievement_id;
															if (!acc[key]) {
																acc[key] = { name: row.achievement_name, id: row.achievement_id, levels: {} };
															}
															if (!acc[key].levels[row.level]) {
																acc[key].levels[row.level] = [];
															}
															acc[key].levels[row.level].push(row);
															return acc;
														}, {})
													).map((group, groupIndex, groupsArray) => {
														const sortedLevels = Object.entries(group.levels)
															.sort(([a], [b]) => Number(a) - Number(b));

														return (
															<Box key={group.id} margin={{ bottom: groupIndex < groupsArray.length - 1 ? "xxl" : "none" }}>
																{sortedLevels.map(([level, data], index) => (
																	<Box key={level}>
																		<Box margin={{ top: index === 0 ? "xs" : "l", bottom: "m" }}>
																			{this.renderStatsTable(group.name, data, group.id, level)}
																		</Box>

																	</Box>
																))}

																{groupIndex < groupsArray.length - 1 && (
																	<div style={{
																		height: 2,
																		backgroundColor: "var(--color-border-divider, #d4d4d4)",
																		margin: "48px 0",
																	}} />
																)}
															</Box>
														);
													})}
												</Box>
											) : (
												<Box textAlign="center" color="text-label" padding="l">
													No progressive achievements data
												</Box>
											),
										},
										{
											label: `Daily Progressive Achievements ${dailyProgressiveStatistics.length > 0 ? `(${dailyProgressiveStatistics.length})` : ''}`,
											id: "daily_progressive",
											content: dailyProgressiveStatistics.length > 0 ? (
												<Box margin={{ top: "m" }}>
													{Object.values(
														dailyProgressiveStatistics.reduce((acc, row) => {
															const key = row.achievement_id;
															if (!acc[key]) {
																acc[key] = { name: row.achievement_name, id: row.achievement_id, levels: {} };
															}
															if (!acc[key].levels[row.level]) {
																acc[key].levels[row.level] = [];
															}
															acc[key].levels[row.level].push(row);
															return acc;
														}, {})
													).map((group, groupIndex, groupsArray) => {
														const sortedLevels = Object.entries(group.levels)
															.sort(([a], [b]) => Number(a) - Number(b));

														return (
															<Box key={group.id} margin={{ bottom: groupIndex < groupsArray.length - 1 ? "xxl" : "none" }}>
																{sortedLevels.map(([level, data], index) => (
																	<Box key={level}>
																		<Box margin={{ top: index === 0 ? "xs" : "l", bottom: "m" }}>
																			{this.renderStatsTable(group.name, data, group.id, level, "Day", true)}
																		</Box>

																	</Box>
																))}

																{groupIndex < groupsArray.length - 1 && (
																	<div style={{
																		height: 2,
																		backgroundColor: "var(--color-border-divider, #d4d4d4)",
																		margin: "48px 0",
																	}} />
																)}
															</Box>
														);
													})}
												</Box>
											) : (
												<Box textAlign="center" color="text-label" padding="l">
													No daily progressive achievements data
												</Box>
											),
										},
									]}
									variant="container"
								/>

						</> : <></>}


					</>
				)}

			</>
		);
	};

	render() {
		const { achievement } = this.props;

		return (
			<>
				<Modal
					visible={this.state.exportModalVisible}
					onDismiss={() => this.setState({ exportModalVisible: false, exportError: "" })}
					header="Export achievements"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button disabled={this.state.exportLoading} onClick={this.resetExportFilters}>
									Clear filters
								</Button>
								<Button variant="link" disabled={this.state.exportLoading} onClick={() => this.setState({ exportModalVisible: false, exportError: "" })}>
									Cancel
								</Button>
								<Button
									variant="primary"
									loading={this.state.exportLoading}
									disabled={
										this.state.exportCreatedAtRange?.type !== "absolute"
										|| !this.state.exportCreatedAtRange?.startDate
										|| !this.state.exportCreatedAtRange?.endDate
										|| !this.state.exportColumns.length
									}
									onClick={this.exportAchievements}
								>
									Export
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					<SpaceBetween size="m">
						{this.state.exportError ? <Alert type="error">{this.state.exportError}</Alert> : null}
						<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
							<FormField label="ID" description="Exact achievement ID">
								<Input
									type="number"
									inputMode="numeric"
									value={this.state.exportId}
									onChange={({ detail }) => this.setState({ exportId: detail.value, exportError: "" })}
									placeholder="All IDs"
								/>
							</FormField>
							<FormField label="Name" description="Case-insensitive substring search">
								<Input
									value={this.state.exportName}
									onChange={({ detail }) => this.setState({ exportName: detail.value, exportError: "" })}
									placeholder="All names"
								/>
							</FormField>
						</Grid>
						<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
							<FormField label="Status">
								<Select
									selectedOption={getExportOption(EXPORT_STATUS_OPTIONS, this.state.exportStatus)}
									options={EXPORT_STATUS_OPTIONS}
									onChange={({ detail }) => this.setState({ exportStatus: detail.selectedOption.value, exportError: "" })}
								/>
							</FormField>
							<FormField label="Export format">
								<Select
									selectedOption={getExportOption(EXPORT_FORMAT_OPTIONS, this.state.exportFormat)}
									options={EXPORT_FORMAT_OPTIONS}
									onChange={({ detail }) => this.setState({ exportFormat: detail.selectedOption.value, exportError: "" })}
								/>
							</FormField>
						</Grid>
						<FormField label="Source Type">
							<Multiselect
								selectedOptions={this.state.exportSourceTypes}
								options={EXPORT_SOURCE_TYPE_OPTIONS}
								onChange={({ detail }) => this.setState({ exportSourceTypes: detail.selectedOptions, exportError: "" })}
								placeholder="All source types"
							/>
						</FormField>
						<FormField label="Achievement Type">
							<Multiselect
								selectedOptions={this.state.exportAchievementTypes}
								options={EXPORT_ACHIEVEMENT_TYPE_OPTIONS}
								onChange={({ detail }) => this.setState({ exportAchievementTypes: detail.selectedOptions, exportError: "" })}
								placeholder="All achievement types"
							/>
						</FormField>
						<FormField label="Prize Type" description="Applied to each progressive level or daily milestone">
							<Multiselect
								selectedOptions={this.state.exportPrizeTypes}
								options={EXPORT_PRIZE_TYPE_OPTIONS}
								onChange={({ detail }) => this.setState({ exportPrizeTypes: detail.selectedOptions, exportError: "" })}
								placeholder="All prize types"
							/>
						</FormField>
						<FormField label="Export Columns" description="Choose which columns will be included in the downloaded file">
							<Multiselect
								selectedOptions={this.state.exportColumns}
								options={EXPORT_COLUMN_OPTIONS}
								onChange={({ detail }) => this.setState({ exportColumns: detail.selectedOptions, exportError: "" })}
								placeholder="Select export columns"
							/>
						</FormField>
						<FormField label="Created At *" description="Required. Filters achievements by creation date">
							<DateRangePicker
								value={this.state.exportCreatedAtRange}
								onChange={({ detail }) => this.setState({ exportCreatedAtRange: detail.value, exportError: "" })}
								i18nStrings={EXPORT_DATE_RANGE_I18N}
								relativeOptions={[]}
								dateOnly
								placeholder="Select creation date range"
								isValidRange={validateExportDateRange}
							/>
						</FormField>
						<FormField label="Active Period" description="Optional. Includes achievements whose active interval overlaps the selected period">
							<DateRangePicker
								value={this.state.exportActivePeriodRange}
								onChange={({ detail }) => this.setState({ exportActivePeriodRange: detail.value, exportError: "" })}
								i18nStrings={EXPORT_DATE_RANGE_I18N}
								relativeOptions={[]}
								dateOnly
								placeholder="All active periods"
								isValidRange={validateExportDateRange}
							/>
						</FormField>
					</SpaceBetween>
				</Modal>
				{achievement.loaded && achievement.error.text.length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p">{achievement.error.text}</Box>
							<hr />
							<Box variant="p">
								request error code: {achievement.error.code} | request timestamp: {achievement.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}

				<Tabs
					activeTabId={this.state.activeTabId}
					onChange={({ detail }) => this.setState({ activeTabId: detail.activeTabId })}
					tabs={[
						{
							label: "Achievement",
							id: "achievement",
							content: (
								<Table
									stickyHeader
									trackBy="id"
									loading={!achievement.loaded}
									sortingColumn={{ sortingField: achievement.sorting.sortingColumn.column }}
									sortingDescending={achievement.sorting.isDescending}
									onSortingChange={(sorting) => {
										this.props.setAchievement({
											...achievement,
											sorting: {
												...achievement.sorting,
												sortingColumn: {
													order: !sorting.detail.isDescending ? "ASC" : "DESC",
													column: sorting.detail.sortingColumn.sortingField,
												},
												isDescending: sorting.detail.isDescending,
											},
										});
										setTimeout(() => this.props.Interface.loadAchievementList(), 250);
									}}
									columnDefinitions={[
										{
											id: "id",
											header: "ID",
											sortingField: "id",
											cell: (item) => (
												<Button
													iconName="status-in-progress"
													variant="normal"
													onClick={() => {
														const typeList = [
															{ value: "deposit", label: "Deposit" },
															{ value: "withdrawal", label: "Withdrawal" },
															{ value: "verify", label: "Verify" },
															{ value: "email_verified", label: "Email verified" },
															{ value: "phone_verified", label: "Phone verified" },
															{ value: "tg_verified", label: "Telegram verified" },
															{ value: "vip_lvl", label: "Vip lvl" },
															{ value: "spins", label: "Spins" },
															{ value: "mobile", label: "Mobile" },
															{ value: "referral", label: "Referral" },
															{ value: "tasks", label: "Tasks" },
														].find((type) => type.value === item.sourceType);

														const cycleList = [
															{ value: "replay", label: "Replay" },
															{ value: "not", label: "Not repeatable" },
														].find((cycle) => cycle.value === item.cycle);

														const cycleTimerList = [
															{ value: "hour", label: "Hour" },
															{ value: "day", label: "Day" },
														].find((cycle) => cycle.value === item.cycleUnit);

														const triggerType = [
															{ value: "crypto", label: "Crypto" },
															{ value: "fiat", label: "Fiat" },
															{ value: "both", label: "Both" },
														];

														const rewardType = [
															{ value: "money", label: "Money" },
															{ value: "tickets", label: "Tickets" },
															{ value: "freespins", label: "Freespins" },
															{ value: "chest", label: "Chest" },
														];

														const triggerConditions = {};

														if (item.type === "deposit" || item.type === "withdrawal") {
															if (Object.keys(JSON.parse(item.triggerConditions)).length > 0) {
																const trigger = JSON.parse(item.triggerConditions)[item.type];
																triggerConditions.amount = trigger.amount / 100;
																triggerConditions.triggerType = triggerType.find((el) => el.value === trigger.type);
															}
														}

														this.props.setAchievementEditModal({
															...this.props.achievementEditModal,
															show: true,
															...item,
															active: item.active ? { value: "1", label: "Enabled" } : { value: "0", label: "Disabled" },
															reward_tickets: item.rewardTickets,
															sourceType: typeList,
															cycle: cycleList,
															type: { value: item.type, label: item.type },
															levels: [],
															startDate: item?.start_time?.replace("_", "T"),
															endDate: item?.end_time?.replace("_", "T"),
															cohort: [],
															rewardValue: item.rewardType === "chest" ? "" : item.rewardType === "money" ? item.rewardValue / 100 : item.rewardValue,
															chestRewards: [],
															cycleUnit: cycleTimerList,
															rewardType: item.rewardType ? rewardType.find((element) => element.value === item.rewardType) : {},
															...triggerConditions,
															dailyProgressiveConfigRaw: item.dailyProgressiveConfig,
														});
													}}
												>
													{item.id}
												</Button>
											),
										},
										{
											width: 70,
											id: "image",
											header: "Image",
											sortingField: "image",
											cell: (item) => (
												<ProgressiveImage src={item.image} placeholder={Poster}>
													{(src) => <img style={{ width: "70px" }} src={src} alt={item.name} />}
												</ProgressiveImage>
											),
										},
										{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
										{ id: "description", header: "Description", sortingField: "description", cell: (item) => item.description },
										{ id: "sourceType", header: "Source type", sortingField: "sourceType", cell: (item) => item.sourceType },
										{ id: "type", header: "Type", sortingField: "type", cell: (item) => item.type },
										{ id: "count", header: "Count", sortingField: "count", cell: (item) => item.count },
										{ id: "rewardValue", header: "Reward value", sortingField: "rewardValue", cell: (item) => item.rewardType === "money" ? item.rewardValue / 100 : item.rewardValue },
										{ id: "link", header: "Link", sortingField: "link", cell: (item) => item.link },
										{ id: "active", header: "Active", sortingField: "active", cell: (item) => item.active },
										{
											id: "createdAt",
											header: "Created at",
											sortingField: "createdAt",
											cell: (item) => <>{new Date(item.createdAt).toLocaleDateString() + " " + new Date(item.createdAt).toLocaleTimeString()}</>,
										},
									]}
									items={achievement.data}
									pagination={
										<Pagination
											disabled={!achievement.loaded}
											currentPageIndex={achievement.pagination.current}
											pagesCount={achievement.pagination.total}
											onChange={(page) => {
												this.props.setAchievement({
													...achievement,
													pagination: { ...achievement.pagination, current: page.detail.currentPageIndex },
												});
												setTimeout(() => this.props.Interface.loadAchievementList(), 250);
											}}
										/>
									}
									preferences={
										<CollectionPreferences
											title="Preferences"
											confirmLabel="Confirm"
											cancelLabel="Cancel"
											preferences={{ pageSize: this.props.config.achievement.perPage }}
											pageSizePreference={{
												title: "Achievement per page",
												options: this.props.config.achievement.preferences.perPage,
											}}
											onConfirm={(preferences) => {
												localStorage.setItem("achievement.perPage", preferences.detail.pageSize);
												this.props.setConfig({
													...this.props.config,
													achievement: { ...this.props.config.achievement, perPage: preferences.detail.pageSize },
												});
												setTimeout(() => this.props.Interface.loadAchievementList(), 250);
											}}
										/>
									}
									header={
										<Header
											description={
												<SpaceBetween direction="horizontal" size="m">
													<StatusIndicator type="success">Active (Enabled): {achievement.statusCounts?.active || 0}</StatusIndicator>
													<StatusIndicator type="stopped">Disabled: {achievement.statusCounts?.disabled || 0}</StatusIndicator>
												</SpaceBetween>
											}
											actions={
												<SpaceBetween direction="horizontal" size="xs">
													<Button
														disabled={!achievement.loaded}
														onClick={() => this.setState({ exportModalVisible: true, exportError: "" })}
													>
														Export
													</Button>
													<Button
														variant="primary"
														iconName="add-plus"
														disabled={!achievement.loaded}
														onClick={() => this.props.setAchivAddModal({ ...this.props.achivAddModal, show: true })}
													>
														Add achievement
													</Button>
												</SpaceBetween>
											}
										>
											Achievement ({achievement.pagination.count})
										</Header>
									}
									empty="No achievement found with requested filter"
								/>
							),
						},
						{
							label: "Analytics",
							id: "analytics",
							content: this.renderAnalyticsTab(),
						},
					]}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const achievement = useRecoilValue(achievementStore);
		const setAchievement = useSetRecoilState(achievementStore);

		const achievementCategory = useRecoilValue(achievementCategoryStore);
		const setAchievementCategory = useSetRecoilState(achievementCategoryStore);

		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);

		const categoryAddModal = useRecoilValue(achievementCategoryAddModalStore);
		const setAddModalCategory = useSetRecoilState(achievementCategoryAddModalStore);

		const categoryEditModal = useRecoilValue(achievementCategoryEditModalStore);
		const setEditCategoryModal = useSetRecoilState(achievementCategoryEditModalStore);

		const achivAddModal = useRecoilValue(achievementAddModalStore);
		const setAchivAddModal = useSetRecoilState(achievementAddModalStore);

		const achievementEditModal = useRecoilValue(achievementEditModalStore);
		const setAchievementEditModal = useSetRecoilState(achievementEditModalStore);

		return (
			<Component
				{...props}
				achievement={achievement}
				setAchievement={setAchievement}
				achievementCategory={achievementCategory}
				setAchievementCategory={setAchievementCategory}
				config={config}
				setConfig={setConfig}
				categoryAddModal={categoryAddModal}
				setAddModalCategory={setAddModalCategory}
				categoryEditModal={categoryEditModal}
				setEditCategoryModal={setEditCategoryModal}
				achivAddModal={achivAddModal}
				setAchivAddModal={setAchivAddModal}
				achievementEditModal={achievementEditModal}
				setAchievementEditModal={setAchievementEditModal}
			/>
		);
	};
}

export default withRecoil(Achievement);
